import React, { useState } from "react";
import { Link } from "gatsby";

export default function SectionRamsay({ slice }) {
  const { primary, items } = slice;
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [activeTags, setActiveTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState(items);

  const allCategories = [
    "All",
    ...new Set(
      items
        .filter((item) => item.category?.text?.trim())
        .map((item) => item.category.text),
    ),
  ];
  const isAllSelected = activeTags.length === 0;

  const handleViewChange = (mode) => {
    if (mode === viewMode) return;
    setViewMode(mode);
  };

  const handleFilterClick = (category) => {
    if (category === "All") {
      setActiveTags([]);
      applyFilters(searchTerm, []);
    } else {
      const newActiveTags = activeTags.includes(category)
        ? activeTags.filter((tag) => tag !== category)
        : [...activeTags, category];
      setActiveTags(newActiveTags);
      applyFilters(searchTerm, newActiveTags);
    }
  };

  const handleSearchChange = (term) => {
    setSearchTerm(term);
    applyFilters(term, activeTags);
  };

  const applyFilters = (search, tags) => {
    let filtered = items;

    if (search.trim()) {
      filtered = filtered.filter((item) =>
        item.title.text.toLowerCase().includes(search.toLowerCase()),
      );
    }

    if (tags.length > 0) {
      filtered = filtered.filter(
        (item) => item.category?.text && tags.includes(item.category.text),
      );
    }

    setFilteredItems(filtered);
  };

  return (
    <section className="bg-white">
      <div className="md:max-w-7xl px-6 xl:px-16 mx-auto py-10">
        <div className="flex flex-col gap-10">
          {/* Header with Filters and View Toggle */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col text-left">
              <h2 className="font-manrope font-medium text-[#1d0642] text-2.5xl sm:text-3xl lg:text-[36px] tracking-[-1.8px] leading-[40px] animate-fade-in mb-4">
                {primary.title.text}
              </h2>
              <p className="font-inter font-normal text-[#434343] text-sm sm:text-lg tracking-[-0.45px] animate-fade-in-delay">
                {primary.descriptor.text}
              </p>
            </div>

            {/* Controls Row */}
            <div className="flex flex-col gap-4">
              <div className="relative w-full">
                <input
                  aria-label="Search"
                  placeholder="Search books..."
                  className="w-full px-4 py-3 pl-8 text-sm font-inter bg-transparent border-0 border-b border-[#e5e5e5] focus:outline-none focus:ring-0 focus:shadow-none focus:border-[#0f0624] transition-all duration-200 placeholder:text-[#9ca3af]"
                  type="text"
                  onChange={(e) => handleSearchChange(e.target.value)}
                  value={searchTerm}
                />
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 text-[#0f0624]">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"
                    />
                  </svg>
                </div>
                {searchTerm && (
                  <button
                    onClick={() => handleSearchChange("")}
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 text-[#9ca3af] hover:text-[#6b7280] transition-colors duration-200 p-1"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Filters and Display Toggle Row */}
              <div className="flex items-center justify-between gap-4">
                {/* Filter Pills - Left - Only show if there are categories */}
                {allCategories.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto HideScrollBar pb-1">
                    {allCategories.map((category) => {
                      const isActive =
                        category === "All"
                          ? isAllSelected
                          : activeTags.includes(category);

                      return (
                        <button
                          key={category}
                          onClick={() => handleFilterClick(category)}
                          className={`whitespace-nowrap px-3 py-1.5 text-xs uppercase tracking-wide font-mono font-medium rounded-[4px] border transition-colors duration-200 ${
                            isActive
                              ? "bg-purple-2300 text-gray-100 border-purple-2300"
                              : "bg-gray-1500 text-gray-600 border-gray-1400 hover:bg-[#F3F2F9] hover:text-purple-1100"
                          }`}
                        >
                          {category}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Results Count Row */}
              <div className="flex justify-between items-center">
                <span className="text-sm font-inter text-gray-500">
                  {filteredItems.length} of {items.length} books
                  {searchTerm && (
                    <span className="ml-1"> matching "{searchTerm}"</span>
                  )}
                </span>
              </div>
              {/* View Toggle */}
              <div className="inline-flex bg-[#f5f5f5] rounded-lg p-1 flex-shrink-0 self-end relative">
                {/* Sliding Background */}
                <div
                  className={`absolute top-1 bottom-1 bg-white rounded-md shadow-sm transition-all duration-300 ease-in-out ${
                    viewMode === "grid"
                      ? "left-1 right-1/2 mr-0.5"
                      : "left-1/2 right-1 ml-0.5"
                  }`}
                />

                <button
                  onClick={() => handleViewChange("grid")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-manrope font-medium rounded-md transition-all duration-200 relative z-10 ${
                    viewMode === "grid"
                      ? "text-[#0f0624]"
                      : "text-[#595959] hover:text-[#434343]"
                  }`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z" />
                  </svg>
                  <span className="hidden sm:inline">Grid</span>
                </button>
                <button
                  onClick={() => handleViewChange("list")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-manrope font-medium rounded-md transition-all duration-200 relative z-10 ${
                    viewMode === "list"
                      ? "text-[#0f0624]"
                      : "text-[#595959] hover:text-[#434343]"
                  }`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"
                    />
                  </svg>
                  <span className="hidden sm:inline">List</span>
                </button>
              </div>
            </div>
          </div>

          {/* Books Grid View */}
          {viewMode === "grid" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredItems.map((item, index) => {
                // const image = getImage(item.image);
                return (
                  <Link
                    key={index}
                    to={`/${item.link.uid}`}
                    className="flex flex-col gap-3 group cursor-pointer animate-fade-in-up"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {/* Book Cover */}
                    <div className="bg-[#ededed] h-[395px] w-full overflow-hidden rounded-lg relative transition-all duration-300 group-hover:shadow-lg">
                      {item.image?.url && (
                        <img
                          src={item.image.url}
                          alt={item.image.alt || item.title.text}
                          className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                        />
                      )}
                    </div>

                    {/* Category Tag */}
                    {item.category?.text?.trim() && (
                      <div
                        className={`flex flex-row justify-start gap-1 overflow-x-auto scroll-smooth no-scrollbar`}
                      >
                        <span
                          className="whitespace-nowrap px-2 py-[2px] text-2xs uppercase tracking-wide font-mono font-medium rounded-[4px] border ${
                  bg-gray-1500 text-gray-600 border-gray-1400"
                        >
                          {item.category.text}
                        </span>
                      </div>
                    )}
                    {/* Book Title */}
                    <h3 className="font-inter font-normal text-[#0f0624] text-xl sm:text-xl tracking-[-0.6px] leading-[32px] group-hover:text-[#1d0642] transition-colors duration-200">
                      {item.title.text}
                    </h3>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Books List View */}
          {viewMode === "list" && (
            <div className="flex flex-col gap-4">
              {filteredItems.map((item, index) => {
                // const image = getImage(item.image);

                return (
                  <Link
                    key={index}
                    to={`/${item.link.uid}`}
                    className="flex gap-6 p-6 rounded-lg border border-[#f5f5f5] group cursor-pointer transition-all duration-300 hover:border-[#d9d9d9] hover:shadow-sm animate-fade-in-up"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {/* Book Cover - Smaller in list view */}
                    <div className="bg-[#ededed] w-24 h-32 flex-shrink-0 overflow-hidden rounded-md">
                      {item.image?.url && (
                        <img
                          src={item.image.url}
                          alt={item.image.alt || item.title.text}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex flex-col gap-3 justify-between">
                      <div className="flex flex-col gap-2">
                        <h3 className="font-inter font-normal text-[#0f0624] text-xl tracking-[-0.6px] leading-[28px] group-hover:text-[#1d0642] transition-colors duration-200">
                          {item.title.text}
                        </h3>
                        <p className="font-inter font-normal text-[#595959] text-sm leading-[22px] line-clamp-2">
                          {item.description.text}
                        </p>
                      </div>

                      {/* Category Tag - Only show if category exists */}
                      {item.category?.text?.trim() && (
                        <div>
                          <span
                            className="whitespace-nowrap px-2 py-[2px] text-2xs uppercase tracking-wide font-mono font-medium rounded-[4px] border ${
                  bg-gray-1500 text-gray-600 border-gray-1400"
                          >
                            {item.category.text}
                          </span>
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes sparkle-1 {
          0%,
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1) rotate(90deg);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
        }

        .animate-fade-in-delay {
          animation: fade-in 0.6s ease-out 0.2s forwards;
          opacity: 0;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
          opacity: 0;
        }

        .animate-sparkle-1 {
          animation: sparkle-1 1.5s ease-in-out infinite;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
}
